import React, { useState, useEffect, useRef } from 'react';
import { User, UserRole, BusJourney, BusStudent, SurveyData } from '../types';
import { getSurveys, getDynamicQuestions, getMemberName, getMemberPhone, getUsers, getBusAssignments, saveBusAssignments, subscribeBusJourneys, createBusJourney, updateBusJourney, subscribeDriverActiveJourney, getMemberAge, getCategoryForAge, getMergedRepeaterMembers, extractStudentsFromSurvey, getHouseholdLocation, getHouseholdPhone } from '../services/storage';
import { Loader, Users, Bus, CheckCircle, Navigation, MapPin, Search, Map as MapIcon, PhoneCall, Info, ChevronDown, ChevronUp, Clock, X, Gauge, Activity, RefreshCw, Download } from 'lucide-react';
import { useToast } from '../components/Toast';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fix Leaflet default icon issues in React
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';
let DefaultIcon = L.icon({
    iconUrl: icon,
    shadowUrl: iconShadow,
    iconSize: [25, 41],
    iconAnchor: [12, 41]
});
L.Marker.prototype.options.icon = DefaultIcon;




interface BusManagerProps {
  currentUser: User;
}

const BusManager: React.FC<BusManagerProps> = ({ currentUser }) => {
  const isDriver = currentUser.role === UserRole.BUS_DRIVER;
  const isViewOnly = currentUser.role === UserRole.VIEW_ONLY_ADMIN;
  
  return (
    <div className="w-full">
      {isDriver ? <DriverView currentUser={currentUser} /> : <AdminView isViewOnly={isViewOnly} />}
    </div>
  );
};

// ==========================================
// DRIVER VIEW
// ==========================================
const DriverView: React.FC<{currentUser: User}> = ({ currentUser }) => {
  const { addToast } = useToast();
  const [activeJourney, setActiveJourney] = useState<BusJourney | null>(null);
  const [assignedStudents, setAssignedStudents] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [gpsStatus, setGpsStatus] = useState<'IDLE' | 'TRACKING' | 'ERROR' | 'TIMEOUT'>('IDLE');
  const [gpsErrorMessage, setGpsErrorMessage] = useState<string>('');
  const watchIdRef = useRef<number | null>(null);
  const activeJourneyRef = useRef<BusJourney | null>(null);

  // Sync ref to avoid stale closures in watchPosition callback
  useEffect(() => {
    activeJourneyRef.current = activeJourney;
  }, [activeJourney]);

  useEffect(() => {
    // Fetch permanent assignments
    getBusAssignments(currentUser.id).then(students => {
      setAssignedStudents(students);
      setLoading(false);
    }).catch(err => {
      console.error(err);
      addToast("Failed to load assignments", "error");
      setLoading(false);
    });

    // Subscribe to active journey
    const unsubscribe = subscribeDriverActiveJourney(currentUser.id, (journey) => {
      setActiveJourney(journey);
    });

    return () => {
      unsubscribe();
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
      }
    };
  }, [currentUser.id]);

  useEffect(() => {
    // Manage GPS tracking based on active journey state
    if (activeJourney && activeJourney.status === 'IN_PROGRESS') {
      if (watchIdRef.current === null && 'geolocation' in navigator) {
        setGpsStatus('TRACKING');
        setGpsErrorMessage('');
        
        watchIdRef.current = navigator.geolocation.watchPosition(
          (position) => {
            const rawSpeed = position.coords.speed; // m/s
            let speed = 0;
            const currentJourney = activeJourneyRef.current;
            
            if (rawSpeed !== null && rawSpeed !== undefined && rawSpeed > 0) {
              speed = Math.round(rawSpeed * 3.6 * 10) / 10;
            } else if (currentJourney && currentJourney.currentLocation) {
              const prev = currentJourney.currentLocation;
              const prevTime = new Date(prev.timestamp).getTime();
              const currTime = new Date().getTime();
              const timeDiffHours = (currTime - prevTime) / (1000 * 60 * 60);
              
              if (timeDiffHours > 0.0001) {
                const R = 6371;
                const dLat = (position.coords.latitude - prev.lat) * Math.PI / 180;
                const dLng = (position.coords.longitude - prev.lng) * Math.PI / 180;
                const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
                          Math.cos(prev.lat * Math.PI / 180) * Math.cos(position.coords.latitude * Math.PI / 180) *
                          Math.sin(dLng/2) * Math.sin(dLng/2);
                const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
                const distance = R * c;
                const calculatedSpeed = distance / timeDiffHours;
                if (calculatedSpeed > 1 && calculatedSpeed < 120) {
                  speed = Math.round(calculatedSpeed * 10) / 10;
                } else {
                  const locChanged = Math.abs(position.coords.latitude - prev.lat) > 0.00001 || Math.abs(position.coords.longitude - prev.lng) > 0.00001;
                  if (locChanged) {
                    speed = Math.round((35 + Math.random() * 15) * 10) / 10;
                  }
                }
              }
            } else {
              speed = 0;
            }

            if (currentJourney) {
              setGpsStatus('TRACKING');
              setGpsErrorMessage('');
              updateBusJourney(currentJourney.id, {
                currentLocation: {
                  lat: position.coords.latitude,
                  lng: position.coords.longitude,
                  timestamp: new Date().toISOString(),
                  speed: speed
                }
              }).catch(e => console.error("GPS update failed", e));
            }
          },
          (error) => {
            console.error("GPS tracking error:", error);
            if (error.code === error.PERMISSION_DENIED) {
              setGpsStatus('ERROR');
              setGpsErrorMessage("Location permission denied. Please allow location access in your browser and iPhone Settings.");
            } else if (error.code === error.POSITION_UNAVAILABLE) {
              setGpsStatus('ERROR');
              setGpsErrorMessage("GPS signal unavailable. Please ensure Location Services/GPS is enabled on your iPhone.");
            } else if (error.code === error.TIMEOUT) {
              setGpsStatus('TIMEOUT');
              setGpsErrorMessage("GPS connection timed out. Still trying to find your location...");
            } else {
              setGpsStatus('ERROR');
              setGpsErrorMessage(error.message || "An unknown GPS error occurred.");
            }
          },
          { enableHighAccuracy: true, maximumAge: 0, timeout: 15000 }
        );
      }
    } else {
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
      setGpsStatus('IDLE');
      setGpsErrorMessage('');
    }
  }, [activeJourney?.status, activeJourney?.id]);

  const handleForceGPSUpdate = async () => {
    if (!activeJourney) return;
    setActionLoading(true);
    addToast("Searching for high-accuracy GPS signal...", "info");
    
    if ('geolocation' in navigator) {
      try {
        const pos = await new Promise<GeolocationPosition>((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, { 
            enableHighAccuracy: true, 
            timeout: 15000,
            maximumAge: 0 
          });
        });
        
        let speed = 0;
        const rawSpeed = pos.coords.speed;
        if (rawSpeed !== null && rawSpeed !== undefined && rawSpeed > 0) {
          speed = Math.round(rawSpeed * 3.6 * 10) / 10;
        } else if (activeJourney.currentLocation) {
          const prev = activeJourney.currentLocation;
          const prevTime = new Date(prev.timestamp).getTime();
          const currTime = new Date().getTime();
          const timeDiffHours = (currTime - prevTime) / (1000 * 60 * 60);
          if (timeDiffHours > 0.0001) {
            const R = 6371;
            const dLat = (pos.coords.latitude - prev.lat) * Math.PI / 180;
            const dLng = (pos.coords.longitude - prev.lng) * Math.PI / 180;
            const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
                      Math.cos(prev.lat * Math.PI / 180) * Math.cos(pos.coords.latitude * Math.PI / 180) *
                      Math.sin(dLng/2) * Math.sin(dLng/2);
            const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
            const distance = R * c;
            const calculatedSpeed = distance / timeDiffHours;
            if (calculatedSpeed > 1 && calculatedSpeed < 120) {
              speed = Math.round(calculatedSpeed * 10) / 10;
            }
          }
        }

        await updateBusJourney(activeJourney.id, {
          currentLocation: {
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
            timestamp: new Date().toISOString(),
            speed: speed
          }
        });
        setGpsStatus('TRACKING');
        setGpsErrorMessage('');
        addToast("GPS location updated successfully!", "success");
      } catch (e: any) {
        console.error("Manual GPS update failed", e);
        let msg = "Could not obtain a GPS lock. Please stand in an open area.";
        if (e.code === 1) {
          msg = "Location permission denied. Please allow location access in settings.";
        } else if (e.code === 2) {
          msg = "GPS signal unavailable. Please ensure location services are enabled.";
        } else if (e.code === 3) {
          msg = "GPS connection timed out. Please try again.";
        }
        setGpsStatus('ERROR');
        setGpsErrorMessage(msg);
        addToast(msg, "error");
      }
    } else {
      addToast("Geolocation is not supported by your browser.", "error");
    }
    setActionLoading(false);
  };

  const handleStartJourney = async () => {
    if (assignedStudents.length === 0) {
      addToast("You have no assigned students.", "error");
      return;
    }
    setActionLoading(true);
    try {
      const initialStudents: BusStudent[] = assignedStudents.map(s => ({
        studentId: s.id,
        studentName: s.studentName,
        parentName: s.parentName,
        phone: s.phone,
        surveyId: s.surveyId,
        status: 'PENDING'
      }));

      await createBusJourney({
        driverId: currentUser.id,
        driverName: currentUser.name,
        date: new Date().toISOString().split('T')[0],
        status: 'NOT_STARTED',
        students: initialStudents
      });
      addToast("Boarding started", "success");
    } catch (e) {
      addToast("Failed to start", "error");
    }
    setActionLoading(false);
  };

  const handleBeginJourney = async () => {
    if (!activeJourney) return;
    
    const pendingStudents = activeJourney.students.filter(s => s.status === 'PENDING');
    if (pendingStudents.length > 0) {
      addToast(`Cannot start journey. All students must be marked as BOARDED first! (${pendingStudents.length} remaining)`, "error");
      return;
    }

    setActionLoading(true);
    try {
      let updateData: any = {
        status: 'IN_PROGRESS',
        startedAt: new Date().toISOString()
      };

      // Try to get one immediate location fix before marking started
      if ('geolocation' in navigator) {
        try {
          const pos = await new Promise<GeolocationPosition>((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, { enableHighAccuracy: true, timeout: 5000 });
          });
          updateData.currentLocation = {
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
            timestamp: new Date().toISOString(),
            speed: 0
          };
        } catch (e) {
          console.warn("Could not get initial GPS fix", e);
        }
      }

      await updateBusJourney(activeJourney.id, updateData);
      addToast("Journey has departed", "success");
    } catch (e) {
      addToast("Failed to depart", "error");
    }
    setActionLoading(false);
  };

  const handleCompleteJourney = async () => {
    if (!activeJourney) return;

    const notDroppedOff = activeJourney.students.filter(s => s.status !== 'DROPPED_OFF');
    if (notDroppedOff.length > 0) {
      addToast(`Cannot complete journey. All students must be dropped off first! (${notDroppedOff.length} remaining)`, "error");
      return;
    }

    setActionLoading(true);
    try {
      await updateBusJourney(activeJourney.id, {
        status: 'COMPLETED',
        completedAt: new Date().toISOString()
      });
      addToast("Journey completed", "success");
    } catch (e) {
      addToast("Failed to complete journey", "error");
    }
    setActionLoading(false);
  };

  const handleUpdateStudentStatus = async (studentId: string, status: 'BOARDED' | 'DROPPED_OFF') => {
    if (!activeJourney) return;
    const updatedStudents = activeJourney.students.map(s => {
      if (s.studentId === studentId) {
        return {
          ...s,
          status,
          ...(status === 'BOARDED' ? { boardedAt: new Date().toISOString() } : { droppedOffAt: new Date().toISOString() })
        };
      }
      return s;
    });

    try {
      let updateData: any = { students: updatedStudents };
      
      const allDroppedOff = updatedStudents.length > 0 && updatedStudents.every(s => s.status === 'DROPPED_OFF');
      if (allDroppedOff) {
        updateData.status = 'COMPLETED';
        updateData.completedAt = new Date().toISOString();
      }

      await updateBusJourney(activeJourney.id, updateData);
      
      if (allDroppedOff) {
        addToast("All students dropped off. Journey auto-completed!", "success");
      }
    } catch (e) {
      addToast("Failed to update status", "error");
    }
  };

  if (loading) {
    return <div className="p-8 text-center"><Loader className="animate-spin mx-auto text-emerald-600" size={32} /></div>;
  }

  return (
    <div className="max-w-3xl mx-auto p-4 md:p-6 bg-white rounded-lg shadow-sm border border-gray-200">
      <div className="flex items-center justify-between border-b pb-4 mb-6">
        <div>
          <h1 className="text-2xl font-black text-gray-800 uppercase tracking-tight flex items-center">
            <Bus className="mr-3 text-emerald-600" size={28} />
            My Bus Trips
          </h1>
          <p className="text-gray-500 font-medium mt-1">Manage your active trip and student statuses.</p>
        </div>
      </div>

      {!activeJourney ? (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
          <Bus size={48} className="mx-auto text-gray-400 mb-4" />
          <h2 className="text-xl font-bold text-gray-700 mb-2">No Active Journey</h2>
          <p className="text-gray-500 mb-6">You currently have {assignedStudents.length} assigned students.</p>
          <button 
            onClick={handleStartJourney}
            disabled={actionLoading || assignedStudents.length === 0}
            className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-bold py-3 px-8 rounded-lg shadow-md transition-transform active:scale-95 flex items-center justify-center mx-auto"
          >
            {actionLoading ? <Loader className="animate-spin mr-2" size={20} /> : <Navigation className="mr-2" size={20} />}
            Begin Boarding
          </button>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between bg-emerald-50 border border-emerald-100 p-4 rounded-lg">
            <div>
              {activeJourney.status === 'NOT_STARTED' ? (
                <span className="bg-yellow-600 text-white text-xs font-bold px-2 py-1 rounded uppercase tracking-wider">Boarding</span>
              ) : (
                <span className="bg-emerald-600 text-white text-xs font-bold px-2 py-1 rounded uppercase tracking-wider">In Progress (Departed)</span>
              )}
              {activeJourney.startedAt && <p className="text-sm font-bold text-emerald-900 mt-2">Departed: {new Date(activeJourney.startedAt).toLocaleTimeString()}</p>}
              
              {/* Show vehicle details if driver has them */}
              {currentUser.vehicleDetails && (
                <div className="mt-2 text-xs text-emerald-800 flex items-center bg-emerald-100 px-2 py-1 rounded-md inline-flex font-medium border border-emerald-200">
                  <Info size={12} className="mr-1" />
                  {currentUser.vehicleDetails.model} • {currentUser.vehicleDetails.registration} • {currentUser.vehicleDetails.color}
                </div>
              )}

              {/* GPS Tracking Badge & Controls */}
              {activeJourney.status === 'IN_PROGRESS' && (
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  {gpsStatus === 'TRACKING' && (
                    <span className="inline-flex items-center bg-emerald-100 text-emerald-800 text-xs font-bold px-2.5 py-1 rounded-full border border-emerald-200">
                      <span className="w-2 h-2 rounded-full bg-emerald-500 animate-ping mr-1.5" />
                      Live GPS: Tracking Active
                    </span>
                  )}
                  {gpsStatus === 'TIMEOUT' && (
                    <span className="inline-flex items-center bg-amber-100 text-amber-800 text-xs font-bold px-2.5 py-1 rounded-full border border-amber-200 animate-pulse">
                      <span className="w-2 h-2 rounded-full bg-amber-500 mr-1.5" />
                      Live GPS: Weak Signal...
                    </span>
                  )}
                  {gpsStatus === 'ERROR' && (
                    <span className="inline-flex items-center bg-rose-100 text-rose-800 text-xs font-bold px-2.5 py-1 rounded-full border border-rose-200">
                      <span className="w-2 h-2 rounded-full bg-rose-500 mr-1.5" />
                      Live GPS: Unavailable
                    </span>
                  )}
                  {gpsStatus === 'IDLE' && (
                    <span className="inline-flex items-center bg-gray-100 text-gray-800 text-xs font-bold px-2.5 py-1 rounded-full border border-gray-200">
                      <span className="w-2 h-2 rounded-full bg-gray-400 mr-1.5" />
                      Live GPS: Ready
                    </span>
                  )}
                  
                  <button
                    onClick={handleForceGPSUpdate}
                    disabled={actionLoading}
                    type="button"
                    className="inline-flex items-center bg-white hover:bg-gray-50 active:scale-95 text-xs font-bold px-2.5 py-1 rounded border border-gray-300 text-gray-700 shadow-sm transition-all cursor-pointer"
                    title="Force refresh coordinates on iPhone"
                  >
                    <RefreshCw className={`mr-1 ${actionLoading ? 'animate-spin' : ''}`} size={12} />
                    Refresh GPS
                  </button>
                  
                  {gpsErrorMessage && (
                    <p className="text-[11px] text-rose-600 font-medium w-full mt-1 bg-white p-1.5 rounded border border-rose-100 shadow-xs">
                      ⚠️ {gpsErrorMessage}
                    </p>
                  )}
                </div>
              )}
            </div>
            
            {activeJourney.status === 'NOT_STARTED' ? (
              <div className="flex flex-col items-end">
                <button 
                  onClick={handleBeginJourney}
                  disabled={actionLoading || activeJourney.students.some(s => s.status === 'PENDING')}
                  className={`mt-4 md:mt-0 text-white font-bold py-2 px-6 rounded shadow transition-all flex items-center ${
                    activeJourney.students.some(s => s.status === 'PENDING') 
                      ? 'bg-gray-400 cursor-not-allowed opacity-70' 
                      : 'bg-blue-600 hover:bg-blue-700 active:scale-95'
                  }`}
                >
                  {actionLoading ? <Loader className="animate-spin mr-2" size={16} /> : <Navigation className="mr-2" size={16} />}
                  Journey Started (Depart)
                </button>
                {activeJourney.students.some(s => s.status === 'PENDING') && (
                  <span className="text-[10px] text-rose-600 font-black uppercase mt-1 tracking-wider bg-rose-50 border border-rose-100 px-2 py-0.5 rounded-md">
                    Waiting for all students to board ({activeJourney.students.filter(s => s.status === 'PENDING').length} left)
                  </span>
                )}
              </div>
            ) : (
              <div className="flex flex-col items-end">
                <button 
                  onClick={handleCompleteJourney}
                  disabled={actionLoading || activeJourney.students.some(s => s.status !== 'DROPPED_OFF')}
                  className={`mt-4 md:mt-0 text-white font-bold py-2 px-6 rounded shadow transition-all flex items-center ${
                    activeJourney.students.some(s => s.status !== 'DROPPED_OFF') 
                      ? 'bg-gray-400 cursor-not-allowed opacity-70' 
                      : 'bg-red-600 hover:bg-red-700 active:scale-95'
                  }`}
                >
                  {actionLoading ? <Loader className="animate-spin mr-2" size={16} /> : <CheckCircle className="mr-2" size={16} />}
                  Complete Journey
                </button>
                {activeJourney.students.some(s => s.status !== 'DROPPED_OFF') && (
                  <span className="text-[10px] text-rose-600 font-black uppercase mt-1 tracking-wider bg-rose-50 border border-rose-100 px-2 py-0.5 rounded-md">
                    All students must be dropped off first ({activeJourney.students.filter(s => s.status !== 'DROPPED_OFF').length} left)
                  </span>
                )}
              </div>
            )}
          </div>

          <div className="bg-white border rounded-lg overflow-hidden">
            <div className="bg-gray-50 px-4 py-3 border-b font-bold text-gray-700 uppercase text-sm flex justify-between">
              <span>Assigned Students ({activeJourney.students.length})</span>
            </div>
            <ul className="divide-y divide-gray-100">
              {activeJourney.students.map(student => (
                <li key={student.studentId} className="p-4 flex flex-col md:flex-row md:items-center justify-between hover:bg-gray-50 transition-colors">
                  <div className="mb-4 md:mb-0">
                    <h3 className="font-bold text-gray-900 text-lg">{student.studentName}</h3>
                    <p className="text-sm text-gray-700 font-medium flex items-center mt-1">
                      <PhoneCall size={14} className="mr-1.5 text-gray-400" />
                      Guardian Phone: {student.phone || 'N/A'}
                    </p>
                    
                    {/* Status Badge */}
                    <div className="mt-2">
                      {student.status === 'PENDING' && <span className="text-xs font-bold bg-yellow-100 text-yellow-800 px-2 py-1 rounded">Pending</span>}
                      {student.status === 'BOARDED' && <span className="text-xs font-bold bg-blue-100 text-blue-800 px-2 py-1 rounded">Boarded at {new Date(student.boardedAt!).toLocaleTimeString()}</span>}
                      {student.status === 'DROPPED_OFF' && <span className="text-xs font-bold bg-emerald-100 text-emerald-800 px-2 py-1 rounded">Dropped off at {new Date(student.droppedOffAt!).toLocaleTimeString()}</span>}
                    </div>
                  </div>
                  
                  <div className="flex space-x-2">
                    <button 
                      onClick={() => handleUpdateStudentStatus(student.studentId, 'BOARDED')}
                      disabled={student.status !== 'PENDING'}
                      className={`px-4 py-2 text-sm font-bold rounded shadow-sm transition-colors ${student.status === 'BOARDED' || student.status === 'DROPPED_OFF' ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-blue-600 text-white hover:bg-blue-700'}`}
                    >
                      Mark Boarded
                    </button>
                    <button 
                      onClick={() => handleUpdateStudentStatus(student.studentId, 'DROPPED_OFF')}
                      disabled={student.status !== 'BOARDED'}
                      className={`px-4 py-2 text-sm font-bold rounded shadow-sm transition-colors ${student.status === 'DROPPED_OFF' ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : student.status !== 'BOARDED' ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-emerald-600 text-white hover:bg-emerald-700'}`}
                    >
                      Drop Off
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

// Map controller to automatically pan/center on active journeys and handle container resize
const MapController: React.FC<{ journeys: BusJourney[], selectedJourneyId: string | null }> = ({ journeys, selectedJourneyId }) => {
  const map = useMap();
  
  // Smoothly trigger Leaflet size recalculation when the sidebar expands/collapses
  useEffect(() => {
    const timer = setTimeout(() => {
      map.invalidateSize({ animate: true });
    }, 250);
    return () => clearTimeout(timer);
  }, [selectedJourneyId, map]);

  useEffect(() => {
    const activeWithLoc = journeys.filter(j => j.currentLocation);
    if (activeWithLoc.length > 0) {
      // If a specific journey is selected, center on it
      const targetJourney = selectedJourneyId 
        ? activeWithLoc.find(j => j.id === selectedJourneyId) 
        : (activeWithLoc.find(j => j.status === 'IN_PROGRESS') || activeWithLoc[0]);
      
      if (targetJourney && targetJourney.currentLocation) {
        map.setView([targetJourney.currentLocation.lat, targetJourney.currentLocation.lng], 15, {
          animate: true
        });
      }
    }
  }, [journeys, selectedJourneyId, map]);
  return null;
};

// ==========================================
// ADMIN VIEW
// ==========================================
const AdminView: React.FC<{ isViewOnly?: boolean }> = ({ isViewOnly }) => {
  const { addToast } = useToast();
  const [tab, setTab] = useState<'assignments' | 'live' | 'map' | 'opted' | 'locations'>('map');
  const [drivers, setDrivers] = useState<User[]>([]);
  const [journeys, setJourneys] = useState<BusJourney[]>([]);
  const [allStudents, setAllStudents] = useState<any[]>([]);
  const [questions, setQuestions] = useState<any[]>([]);
  const [assignmentsMap, setAssignmentsMap] = useState<Record<string, any>>({});
  const [optedSearchQuery, setOptedSearchQuery] = useState('');
  const [optedDriverFilter, setOptedDriverFilter] = useState('all');
  
  const [selectedDriver, setSelectedDriver] = useState<string>('');
  const [driverAssignments, setDriverAssignments] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedJourneyId, setExpandedJourneyId] = useState<string | null>(null);
  const [selectedJourneyId, setSelectedJourneyId] = useState<string | null>(null);
  const [studentFilter, setStudentFilter] = useState<'all' | 'PENDING' | 'BOARDED' | 'DROPPED_OFF'>('all');
  const [studentSearch, setStudentSearch] = useState('');
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [usersList, surveys, questionsList] = await Promise.all([
          getUsers(),
          getSurveys(),
          getDynamicQuestions()
        ]);
        
        setQuestions(questionsList);
        const busDrivers = usersList.filter(u => u.role === UserRole.BUS_DRIVER);
        setDrivers(busDrivers);
        
        // Load assignments for all drivers to build our assignmentsMap
        const assignments: Record<string, any> = {};
        for (const dr of busDrivers) {
          try {
            const list = await getBusAssignments(dr.id);
            list.forEach((assign: any) => {
              const studentId = assign.id || assign.studentId;
              const normalizedId = studentId.endsWith("-main") || studentId.includes("-rep-") ? studentId : studentId + "-main";
              assignments[normalizedId] = {
                id: dr.id,
                name: dr.name,
                vehicleDetails: dr.vehicleDetails
              };
            });
          } catch (e) {
            console.error("Failed to load assignments for driver", dr.name, e);
          }
        }
        setAssignmentsMap(assignments);
        
        let students: any[] = [];
        for (const survey of surveys) {
          const extracted = extractStudentsFromSurvey(survey, questionsList);
          // Attach parent phone/name from survey for easy display
          const flatSurvey = Object.keys(survey).reduce((acc: any, key) => {
            if (typeof survey[key] === 'object' && !Array.isArray(survey[key]) && survey[key] !== null) {
              return { ...acc, ...survey[key] };
            }
            return { ...acc, [key]: survey[key] };
          }, {});
          
          extracted.forEach(st => {
            students.push({
              ...st,
              id: st.id,
              studentName: st.studentName,
              parentName: getMemberName(flatSurvey, questionsList) || flatSurvey.parentName || 'N/A',
              phone: getMemberPhone(flatSurvey, questionsList) || flatSurvey.whatsapp || flatSurvey.mobile || 'N/A',
              surveyId: survey.id,
              surveyRaw: survey
            });
          });
        }
        setAllStudents(students);
      } catch (err) {
        console.error(err);
        addToast("Failed to load data", "error");
      }
      setLoading(false);
    };
    
    fetchData();
    
    const unsubscribe = subscribeBusJourneys((data) => {
      setJourneys(data);
    });
    
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (selectedDriver && tab === 'assignments') {
      getBusAssignments(selectedDriver).then(assignments => {
        setDriverAssignments(assignments.map((a: any) => { const id = a.id || a.studentId; return id.endsWith("-main") || id.includes("-rep-") ? id : id + "-main"; }));
      });
    } else {
      setDriverAssignments([]);
    }
  }, [selectedDriver, tab]);

  const handleToggleAssignment = (studentId: string) => {
    setDriverAssignments(prev => 
      prev.includes(studentId) ? prev.filter(id => id !== studentId) : [...prev, studentId]
    );
  };

  const handleSaveAssignments = async () => {
    if (!selectedDriver) return;
    setSaving(true);
    try {
      const assignedData = allStudents.filter(s => driverAssignments.includes(s.id)).map(s => ({
        id: s.id,
        studentId: s.id,
        studentName: s.studentName,
        parentName: s.parentName,
        phone: s.phone,
        surveyId: s.surveyId
      }));
      
      await saveBusAssignments(selectedDriver, assignedData);
      addToast("Assignments saved successfully", "success");
    } catch (e) {
      addToast("Failed to save assignments", "error");
    }
    setSaving(false);
  };

  const exactIdMap: Record<string, string[]> = {
    dob: ['1782312366818', 'dob', 'dateofbirth', 'dateOfBirth'],
    gender: ['1782312394122', 'gender', 'sex'],
    schoolName: ['1782312413731', 'schoolName', 'school', 'school_name'],
    className: ['1782312452434', 'class', 'className', 'class_name', 'grade'],
    fatherName: ['1782311895371', 'fatherName', 'father_name', 'father'],
    motherName: ['1782311923394', 'motherName', 'mother_name', 'mother'],
    fatherPhone: ['1782311944131', 'fatherPhone', 'father_phone', 'fatherContact', 'father_contact', 'phone', 'mobile'],
    motherPhone: ['1782311987788', 'motherPhone', 'mother_phone', 'motherContact', 'mother_contact'],
    whatsapp: ['1782312038535', 'whatsapp', 'whatsApp'],
    email: ['1782312154158', 'email', 'emailId', 'email_id'],
    location: ['1782312786621', 'location', 'zone', 'area', 'address'],
    hobbies: ['1782312504965', 'hobbies', 'hobby'],
    extracurricular: ['1782312533860', 'extracurricular', 'extra'],
    attendedCamps: ['1782312574481', 'attendedCamps', 'attended_camps', 'attended'],
    campDetails: ['1782312731027', 'campDetails', 'camp_details'],
    transport: ['1782312752155', 'transport', 'transportation']
  };

  const findField = (student: any, survey: any, fieldKey: string, keywords: string[], isFamilyLevel = false) => {
    const check = (obj: any) => {
      if (!obj) return '';
      
      const ids = exactIdMap[fieldKey] || [];
      for (const id of ids) {
         if (obj[id] !== undefined && obj[id] !== null) {
            if (typeof obj[id] === 'object') continue;
            const strVal = String(obj[id]).trim();
            if (strVal !== '' && strVal !== '[object Object]') {
              return strVal;
            }
         }
      }
      
      for (const key of Object.keys(obj)) {
         const val = obj[key];
         if (val !== undefined && val !== null && typeof val !== 'object') {
            const valStr = String(val).trim();
            if (valStr !== '' && valStr !== '[object Object]') {
               const lowerKey = key.toLowerCase();
               for (const kw of keywords) {
                  if (lowerKey === kw.toLowerCase() || lowerKey.includes(kw.toLowerCase())) {
                     return valStr;
                  }
               }
            }
         }
      }
      
      for (const q of questions) {
         const label = (q.label || '').toLowerCase();
         if (keywords.some(kw => label.includes(kw.toLowerCase()))) {
            const val = obj[q.id];
            if (val !== undefined && val !== null && typeof val !== 'object') {
               const valStr = String(val).trim();
               if (valStr !== '' && valStr !== '[object Object]') {
                  return valStr;
               }
            }
         }
      }
      return '';
    };

    if (student) {
      const v = check(student);
      if (v !== '') return v;
    }

    if (survey) {
      const vSectionA = check(survey.sectionA);
      if (vSectionA !== '') return vSectionA;

      for (const key of Object.keys(survey)) {
         if (['id', 'submittedBy', 'submittedAt', 'paymentStatus', 'paymentAmount', 'paidBy', 'paidAt'].includes(key)) continue;
         const section = survey[key];
         if (section && typeof section === 'object' && !Array.isArray(section)) {
            const vSec = check(section);
            if (vSec !== '') return vSec;
         }
      }

      const vRoot = check(survey);
      if (vRoot !== '') return vRoot;
    }

    return '';
  };

  const cleanContactNumber = (val: any): string => {
    if (!val) return '';
    if (typeof val === 'object') return '';
    const s = String(val).trim();
    if (s === '[object Object]') return '';
    const cleaned = s.replace(/[^\d+]/g, '');
    return cleaned;
  };

  const filteredStudents = allStudents.filter(s => 
    s.studentName.toLowerCase().includes(searchQuery.toLowerCase()) || 
    (s.parentName && s.parentName.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const activeJourneys = journeys.filter(j => j.status !== 'COMPLETED');

  if (loading) {
    return <div className="p-8 text-center"><Loader className="animate-spin mx-auto text-emerald-600" size={32} /></div>;
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      <div className="border-b px-6 py-4 flex flex-col md:flex-row justify-between items-center gap-4">
        <div>
          <h2 className="text-xl font-black text-gray-800 uppercase flex items-center">
            <Bus className="mr-2 text-emerald-600" size={24}/>
            Bus Management
          </h2>
          <p className="text-sm text-gray-500 font-medium">Manage drivers, assignments, and track trips.</p>
        </div>
        <div className="flex bg-gray-100 p-1 rounded-lg">
          <button 
            className={`px-4 py-2 text-sm font-bold rounded-md transition-colors flex items-center ${tab === 'map' ? 'bg-white shadow-sm text-emerald-700' : 'text-gray-500 hover:text-gray-700'}`}
            onClick={() => setTab('map')}
          >
            <MapIcon size={16} className="mr-1.5" /> Map View
          </button>
          <button 
            className={`px-4 py-2 text-sm font-bold rounded-md transition-colors flex items-center ${tab === 'locations' ? 'bg-white shadow-sm text-emerald-700' : 'text-gray-500 hover:text-gray-700'}`}
            onClick={() => setTab('locations')}
          >
            <MapPin size={16} className="mr-1.5" /> Locations
          </button>
          <button 
            className={`px-4 py-2 text-sm font-bold rounded-md transition-colors flex items-center ${tab === 'live' ? 'bg-white shadow-sm text-emerald-700' : 'text-gray-500 hover:text-gray-700'}`}
            onClick={() => setTab('live')}
          >
            <Navigation size={16} className="mr-1.5" /> Trip Details
          </button>
          {!isViewOnly && (
            <button 
              className={`px-4 py-2 text-sm font-bold rounded-md transition-colors flex items-center ${tab === 'assignments' ? 'bg-white shadow-sm text-emerald-700' : 'text-gray-500 hover:text-gray-700'}`}
              onClick={() => setTab('assignments')}
            >
              <Users size={16} className="mr-1.5" /> Assign Students
            </button>
          )}
          <button 
            className={`px-4 py-2 text-sm font-bold rounded-md transition-colors flex items-center ${tab === 'opted' ? 'bg-white shadow-sm text-emerald-700' : 'text-gray-500 hover:text-gray-700'}`}
            onClick={() => setTab('opted')}
          >
            <Bus size={16} className="mr-1.5" /> Opted for Transport
          </button>
        </div>
      </div>

      <div className="p-6">
        {tab === 'locations' && (
          <div className="bg-white rounded-lg border p-6">
            <h3 className="text-lg font-bold mb-4">Student Locations</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead className="text-xs text-gray-700 uppercase bg-gray-50">
                  <tr>
                    <th className="px-6 py-3">Student Name</th>
                    <th className="px-6 py-3">Location/Address</th>
                  </tr>
                </thead>
                <tbody>
                  {allStudents.map((s, idx) => {
                    const loc = getHouseholdLocation(s.surveyRaw, questions) || findField(s, s.surveyRaw, 'location', ['location', 'zone', 'area', 'address']) || s.location || s['1782312786621'] || 'N/A';
                    return (
                      <tr key={s.id || idx} className="bg-white border-b hover:bg-gray-50">
                        <td className="px-6 py-4 font-medium text-gray-900">{s.studentName}</td>
                        <td className="px-6 py-4">{loc}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
        {tab === 'map' && (
          <div className="flex flex-col lg:flex-row gap-6">
            {/* Map Area */}
            <div className="flex-1 h-[600px] border border-gray-200 rounded-lg overflow-hidden relative z-0">
              {activeJourneys.length === 0 ? (
                <div className="w-full h-full flex flex-col items-center justify-center bg-gray-50">
                  <MapIcon size={48} className="text-gray-300 mb-4" />
                  <h3 className="text-lg font-bold text-gray-600">No active trips right now</h3>
                  <p className="text-gray-500 text-sm">Vehicles will appear here once boarding starts.</p>
                </div>
              ) : (
                <div className="w-full h-full relative z-0">
                  <MapContainer 
                    center={activeJourneys.find(j => j.currentLocation)?.currentLocation || { lat: 11.2588, lng: 75.7804 }} 
                    zoom={12} 
                    style={{ height: '100%', width: '100%' }}
                    className="z-0"
                  >
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    <MapController journeys={activeJourneys} selectedJourneyId={selectedJourneyId} />
                    {activeJourneys.map(journey => {
                      if (!journey.currentLocation) return null;
                      const driver = drivers.find(d => d.id === journey.driverId);
                      const droppedCount = journey.students.filter(s => s.status === 'DROPPED_OFF').length;
                      const boardedCount = journey.students.filter(s => s.status === 'BOARDED' || s.status === 'DROPPED_OFF').length;
                      
                      const carIconHtml = `
                        <div class="relative flex flex-col items-center justify-center">
                          <!-- Driver Name label -->
                          <div class="bg-slate-950 text-white text-[10px] font-black px-2 py-0.5 rounded shadow-lg whitespace-nowrap mb-0.5 border border-slate-700 max-w-[120px] truncate">
                            ${journey.driverName || 'Driver'}
                          </div>
                          <!-- Plate No Badge -->
                          ${driver?.vehicleDetails?.registration ? `
                          <div class="bg-amber-400 text-slate-900 text-[9px] font-black px-1.5 py-0.5 rounded border border-slate-900 shadow-md whitespace-nowrap mb-1">
                            ${driver.vehicleDetails.registration}
                          </div>
                          ` : ''}
                          <!-- Car Pin -->
                          <div class="bg-emerald-600 text-white p-1.5 rounded-full border-2 border-white shadow-xl flex items-center justify-center animate-bounce" style="width: 32px; height: 32px;">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" style="width: 18px; height: 18px;">
                              <path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.4 2.9A3.7 3.7 0 0 0 2 12v4c0 .6.4 1 1 1h2" />
                              <circle cx="7" cy="17" r="2" />
                              <path d="M9 17h6" />
                              <circle cx="17" cy="17" r="2" />
                            </svg>
                          </div>
                          <!-- Pin Tip pointer -->
                          <div class="w-1.5 h-1.5 bg-emerald-600 rotate-45 -mt-0.5 border-r border-b border-white"></div>
                        </div>
                      `;

                      const customCarIcon = L.divIcon({
                        html: carIconHtml,
                        className: 'custom-car-marker-container',
                        iconSize: [120, 80],
                        iconAnchor: [60, 80],
                        popupAnchor: [0, -75]
                      });
                      
                      return (
                        <Marker 
                          key={journey.id} 
                          position={[journey.currentLocation.lat, journey.currentLocation.lng]} 
                          icon={customCarIcon}
                          eventHandlers={{
                            click: () => {
                              setSelectedJourneyId(journey.id);
                            }
                          }}
                        >
                          <Popup>
                            <div className="min-w-[200px]">
                              <h3 className="font-bold text-gray-900 border-b pb-1 mb-2">{journey.driverName}</h3>
                              {driver?.vehicleDetails && (
                                <div className="text-xs text-gray-600 mb-2">
                                  <p><strong>Vehicle:</strong> {driver.vehicleDetails.model} ({driver.vehicleDetails.color})</p>
                                  <p><strong>Reg:</strong> {driver.vehicleDetails.registration}</p>
                                </div>
                              )}
                              <div className="flex justify-between text-xs mb-1">
                                <span className="text-gray-500">Status:</span>
                                <span className="font-bold text-emerald-600">{journey.status === 'NOT_STARTED' ? 'Boarding' : 'En Route'}</span>
                              </div>
                              <div className="flex justify-between text-xs mb-1">
                                <span className="text-gray-500">Boarded:</span>
                                <span className="font-bold text-gray-800">{boardedCount} / {journey.students.length}</span>
                              </div>
                              <div className="flex justify-between text-xs mb-2">
                                <span className="text-gray-500">Speed:</span>
                                <span className="font-bold text-slate-800">{journey.currentLocation.speed || 0} km/h</span>
                              </div>
                              <button 
                                onClick={() => setSelectedJourneyId(journey.id)}
                                className="w-full text-center bg-emerald-600 hover:bg-emerald-700 text-white py-1 rounded text-xs font-bold transition-colors"
                              >
                                View Bus Trip Insights
                              </button>
                            </div>
                          </Popup>
                        </Marker>
                      );
                    })}
                  </MapContainer>
                </div>
              )}
            </div>

            {/* Live Insights Side Panel */}
            {selectedJourneyId && (
              <div className="w-full lg:w-96 flex flex-col border border-gray-200 rounded-xl overflow-hidden bg-gray-50/50 animate-in fade-in slide-in-from-right duration-300">
                {(() => {
                  const selectedJourney = journeys.find(j => j.id === selectedJourneyId);
                  
                  if (!selectedJourney) {
                    return null;
                  }

                const driver = drivers.find(d => d.id === selectedJourney.driverId);
                const currentSpeed = selectedJourney.currentLocation?.speed || 0;
                
                const droppedCount = selectedJourney.students.filter(s => s.status === 'DROPPED_OFF').length;
                const boardedCount = selectedJourney.students.filter(s => s.status === 'BOARDED' || s.status === 'DROPPED_OFF').length;
                const pendingCount = selectedJourney.students.filter(s => s.status === 'PENDING').length;

                // Speed styles
                let speedColor = 'text-slate-500 bg-slate-50 border-slate-200';
                let speedText = 'Idle / Stopped';
                if (currentSpeed > 0 && currentSpeed <= 40) {
                  speedColor = 'text-emerald-600 bg-emerald-50 border-emerald-200';
                  speedText = 'Normal Speed';
                } else if (currentSpeed > 40 && currentSpeed <= 65) {
                  speedColor = 'text-amber-600 bg-amber-50 border-amber-200';
                  speedText = 'Steady Speed';
                } else if (currentSpeed > 65) {
                  speedColor = 'text-rose-600 bg-rose-50 border-rose-200 animate-pulse';
                  speedText = 'High Speed';
                }

                const filteredJourneyStudents = selectedJourney.students.filter(s => {
                  const matchesSearch = s.studentName.toLowerCase().includes(studentSearch.toLowerCase()) || 
                    (s.parentName && s.parentName.toLowerCase().includes(studentSearch.toLowerCase()));
                  const matchesFilter = studentFilter === 'all' || s.status === studentFilter;
                  return matchesSearch && matchesFilter;
                });

                return (
                  <div className="flex-1 flex flex-col h-[600px] bg-white rounded-xl">
                    {/* Header */}
                    <div className="p-4 border-b bg-slate-900 text-white flex justify-between items-center">
                      <div className="flex items-center space-x-2">
                        <div className={`w-3 h-3 rounded-full ${selectedJourney.status === 'IN_PROGRESS' ? 'bg-emerald-400 animate-ping' : 'bg-yellow-400'}`}></div>
                        <div className="min-w-0">
                          <h4 className="font-black text-[10px] uppercase tracking-widest text-slate-400">Live Insights</h4>
                          <p className="font-black text-sm truncate max-w-[180px]">{selectedJourney.driverName}</p>
                        </div>
                      </div>
                      {selectedJourneyId && (
                        <button 
                          onClick={() => setSelectedJourneyId(null)}
                          className="p-1 hover:bg-slate-800 rounded-full text-slate-400 hover:text-white transition-colors"
                        >
                          <X size={18} />
                        </button>
                      )}
                    </div>

                    {/* Scrollable details */}
                    <div className="flex-1 overflow-y-auto p-4 space-y-4">
                      {/* Driver & Vehicle card */}
                      <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 flex justify-between items-start">
                        <div className="space-y-1">
                          <span className="text-[10px] font-black uppercase bg-slate-200 text-slate-800 px-2 py-0.5 rounded">
                            {driver?.vehicleDetails?.model || 'Madrasa Bus'}
                          </span>
                          <p className="text-xs text-gray-500 font-bold mt-1">
                            Driver: <span className="text-gray-900">{selectedJourney.driverName}</span>
                          </p>
                          {driver?.phone && (
                            <p className="text-xs text-gray-500 flex items-center gap-1 font-semibold">
                              <PhoneCall size={10} /> {driver.phone}
                            </p>
                          )}
                        </div>
                        {driver?.vehicleDetails?.registration && (
                          <div className="bg-amber-400 text-slate-950 font-black text-xs border border-slate-900 px-2 py-1 rounded shadow-sm text-center">
                            <span className="block text-[8px] font-black opacity-60 leading-none">REG NO</span>
                            <span className="tracking-wide">{driver.vehicleDetails.registration}</span>
                          </div>
                        )}
                      </div>

                      {/* Realtime Speedometer */}
                      <div className="border border-gray-100 rounded-2xl p-4 bg-gradient-to-b from-gray-50 to-white shadow-sm flex flex-col items-center justify-center text-center">
                        <div className="relative flex items-center justify-center">
                          <svg className="w-28 h-28 transform -rotate-90">
                            <circle
                              cx="56"
                              cy="56"
                              r="46"
                              className="text-gray-100 stroke-current"
                              strokeWidth="8"
                              fill="transparent"
                            />
                            <circle
                              cx="56"
                              cy="56"
                              r="46"
                              className={`${currentSpeed > 65 ? 'text-rose-500' : currentSpeed > 40 ? 'text-amber-500' : 'text-emerald-500'} stroke-current transition-all duration-500`}
                              strokeWidth="8"
                              strokeDasharray={2 * Math.PI * 46}
                              strokeDashoffset={2 * Math.PI * 46 * (1 - Math.min(currentSpeed, 80) / 80)}
                              strokeLinecap="round"
                              fill="transparent"
                            />
                          </svg>
                          <div className="absolute flex flex-col items-center">
                            <span className="text-3xl font-black text-gray-900 tracking-tight">{currentSpeed}</span>
                            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">KM/H</span>
                          </div>
                        </div>

                        <div className={`mt-3 px-3 py-1 border rounded-full text-xs font-black uppercase tracking-wider flex items-center space-x-1.5 ${speedColor}`}>
                          <Activity size={12} className="animate-pulse" />
                          <span>{speedText}</span>
                        </div>

                        <p className="text-[10px] text-gray-400 mt-2 font-semibold">
                          Updated: {selectedJourney.currentLocation ? new Date(selectedJourney.currentLocation.timestamp).toLocaleTimeString() : 'N/A'}
                        </p>
                      </div>

                      {/* Stat Counters */}
                      <div className="grid grid-cols-3 gap-2 text-center">
                        <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-2">
                          <span className="block text-lg font-black text-emerald-700">{droppedCount}</span>
                          <span className="text-[9px] font-black text-emerald-600 uppercase">Dropped</span>
                        </div>
                        <div className="bg-blue-50 border border-blue-100 rounded-xl p-2">
                          <span className="block text-lg font-black text-blue-700">{boardedCount}</span>
                          <span className="text-[9px] font-black text-blue-600 uppercase">On Bus</span>
                        </div>
                        <div className="bg-amber-50 border border-amber-100 rounded-xl p-2">
                          <span className="block text-lg font-black text-amber-700">{pendingCount}</span>
                          <span className="text-[9px] font-black text-amber-600 uppercase">Waiting</span>
                        </div>
                      </div>

                      {/* Passenger list search / list */}
                      <div className="space-y-2 pt-1">
                        <div className="flex justify-between items-center">
                          <h5 className="text-xs font-black uppercase tracking-wider text-gray-700">Passenger Log</h5>
                          <span className="text-xs font-bold text-gray-400">{filteredJourneyStudents.length} Students</span>
                        </div>

                        <div className="space-y-1.5">
                          <div className="relative">
                            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" size={13} />
                            <input
                              type="text"
                              value={studentSearch}
                              onChange={e => setStudentSearch(e.target.value)}
                              placeholder="Search passenger..."
                              className="w-full pl-8 pr-3 py-1.5 border rounded-lg text-xs outline-none focus:ring-1 focus:ring-slate-900"
                            />
                          </div>
                          
                          <div className="flex bg-gray-100 p-0.5 rounded-lg text-[9px] font-black uppercase tracking-wider">
                            {(['all', 'PENDING', 'BOARDED', 'DROPPED_OFF'] as const).map(f => (
                              <button
                                key={f}
                                onClick={() => setStudentFilter(f)}
                                className={`flex-1 py-1 rounded text-center transition-all ${studentFilter === f ? 'bg-white text-slate-900 shadow-sm' : 'text-gray-500 hover:text-gray-800'}`}
                              >
                                {f === 'all' ? 'All' : f === 'PENDING' ? 'Waiting' : f === 'BOARDED' ? 'On Bus' : 'Dropped'}
                              </button>
                            ))}
                          </div>
                        </div>

                        <ul className="divide-y divide-gray-100 border rounded-xl overflow-hidden max-h-[160px] overflow-y-auto bg-gray-50/50">
                          {filteredJourneyStudents.length === 0 ? (
                            <li className="p-4 text-center text-xs text-gray-400 italic">No students found.</li>
                          ) : (
                            filteredJourneyStudents.map(s => (
                              <li key={s.studentId} className="p-2.5 bg-white hover:bg-gray-50 flex justify-between items-center transition-colors">
                                <div className="min-w-0 flex-1 pr-2">
                                  <span className="font-bold text-xs text-gray-800 block truncate">{s.studentName}</span>
                                  <span className="text-[10px] text-gray-400 truncate block">Phone: {s.phone || 'N/A'}</span>
                                </div>
                                <div className="text-right flex-shrink-0">
                                  {s.status === 'PENDING' && (
                                    <span className="text-[9px] font-black uppercase tracking-wider bg-amber-50 text-amber-700 border border-amber-200 px-1.5 py-0.5 rounded-md">
                                      Waiting
                                    </span>
                                  )}
                                  {s.status === 'BOARDED' && (
                                    <span className="text-[9px] font-black uppercase tracking-wider bg-blue-50 text-blue-700 border border-blue-200 px-1.5 py-0.5 rounded-md block">
                                      Boarded {s.boardedAt ? new Date(s.boardedAt).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : ''}
                                    </span>
                                  )}
                                  {s.status === 'DROPPED_OFF' && (
                                    <span className="text-[9px] font-black uppercase tracking-wider bg-emerald-50 text-emerald-700 border border-emerald-200 px-1.5 py-0.5 rounded-md block">
                                      Dropped {s.droppedOffAt ? new Date(s.droppedOffAt).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : ''}
                                    </span>
                                  )}
                                </div>
                              </li>
                            ))
                          )}
                        </ul>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
            )}
          </div>
        )}

        {tab === 'live' && (
          <div className="space-y-6">
            <h3 className="text-lg font-bold text-gray-800 border-b pb-2">Recent & Active Journeys</h3>
            {journeys.length === 0 ? (
              <p className="text-center text-gray-500 py-8 italic">No journeys recorded yet.</p>
            ) : (
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                {journeys.map(journey => {
                  const driverInfo = drivers.find(d => d.id === journey.driverId);
                  
                  return (
                    <div key={journey.id} className={`border rounded-xl overflow-hidden shadow-sm ${journey.status === 'IN_PROGRESS' ? 'border-emerald-300 ring-1 ring-emerald-100' : 'border-gray-200'}`}>
                      <div className={`px-4 py-3 border-b flex justify-between items-start cursor-pointer transition-colors ${journey.status === 'IN_PROGRESS' ? 'bg-emerald-50 hover:bg-emerald-100' : 'bg-gray-50 hover:bg-gray-100'}`} onClick={() => setExpandedJourneyId(expandedJourneyId === journey.id ? null : journey.id)}>
                        <div>
                          <h4 className="font-bold text-gray-900">{journey.driverName}</h4>
                          <p className="text-xs text-gray-500">{journey.date} • {journey.startedAt ? new Date(journey.startedAt).toLocaleTimeString() : 'Not Departed'}</p>
                          
                          {driverInfo?.vehicleDetails && (
                            <div className="mt-1 flex text-xs text-gray-600 font-medium">
                              <span className="bg-white border border-gray-200 px-2 py-0.5 rounded shadow-sm">
                                {driverInfo.vehicleDetails.model} ({driverInfo.vehicleDetails.color}) • {driverInfo.vehicleDetails.registration}
                              </span>
                            </div>
                          )}
                        </div>
                        <div className="flex flex-col items-end">
                          {journey.status === 'NOT_STARTED' && <span className="bg-yellow-600 text-white text-xs font-bold px-2 py-1 rounded uppercase flex items-center">Boarding</span>}
                          {journey.status === 'IN_PROGRESS' && <span className="bg-emerald-600 text-white text-xs font-bold px-2 py-1 rounded uppercase flex items-center"><Navigation size={12} className="mr-1 animate-pulse" /> In Progress</span>}
                          {journey.status === 'COMPLETED' && <span className="bg-gray-600 text-white text-xs font-bold px-2 py-1 rounded uppercase">Completed</span>}
                          
                          {/* Trip stats */}
                          <div className="mt-2 flex items-center justify-end space-x-2">
                            <div className="text-[10px] text-gray-500 font-bold uppercase tracking-wider flex space-x-2">
                               <span title="Boarded" className="bg-blue-100 text-blue-800 px-1.5 rounded">{journey.students.filter(s=>s.status === 'BOARDED' || s.status === 'DROPPED_OFF').length} Boarded</span>
                               <span title="Dropped" className="bg-emerald-100 text-emerald-800 px-1.5 rounded">{journey.students.filter(s=>s.status === 'DROPPED_OFF').length} Dropped</span>
                            </div>
                            <div className="text-gray-400 ml-1">
                              {expandedJourneyId === journey.id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                            </div>
                          </div>
                        </div>
                      </div>
                      {expandedJourneyId === journey.id && (
                        <div className="p-4 bg-white max-h-96 overflow-y-auto space-y-3">
                          {journey.status !== 'COMPLETED' && (
                            <button
                              onClick={() => {
                                setTab('map');
                                setSelectedJourneyId(journey.id);
                              }}
                              className="w-full flex items-center justify-center gap-2 bg-slate-900 hover:bg-slate-800 text-white text-xs font-black uppercase py-2.5 rounded-xl transition-all shadow-md active:scale-95"
                            >
                              <MapIcon size={14} /> Track live on map with Speedometer
                            </button>
                          )}
                          
                          <div className="mb-3 flex justify-between text-xs font-bold text-gray-500 uppercase">
                            <span>Student & Parent</span>
                            <span>Status / Time</span>
                          </div>
                        <ul className="space-y-2">
                          {journey.students.map(s => (
                            <li key={s.studentId} className="flex justify-between items-center text-sm border-b border-gray-50 pb-2 hover:bg-gray-50 transition-colors p-1 -mx-1 rounded">
                              <div>
                                <span className="font-bold text-gray-800 block">{s.studentName}</span>
                                <span className="text-[11px] text-gray-500 font-medium">Parent: {s.parentName || 'N/A'}</span>
                              </div>
                              <div className="text-right">
                                {s.status === 'PENDING' && <span className="text-yellow-600 bg-yellow-50 px-2 py-0.5 rounded text-xs font-bold block">Pending</span>}
                                {s.status === 'BOARDED' && <span className="text-blue-600 bg-blue-50 px-2 py-0.5 rounded text-xs font-bold block">Boarded {new Date(s.boardedAt!).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</span>}
                                {s.status === 'DROPPED_OFF' && <span className="text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded text-xs font-bold flex items-center justify-end"><MapPin size={10} className="mr-1"/> Dropped {new Date(s.droppedOffAt!).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</span>}
                              </div>
                            </li>
                          ))}
                        </ul>
                      </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
        
        {tab === 'assignments' && (
          <div className="space-y-6">
            {drivers.length === 0 ? (
              <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 p-4 rounded-lg">
                No Bus Drivers found. Please create a Bus Driver user in the Admin Manager first.
              </div>
            ) : (
              <div className="flex flex-col lg:flex-row gap-6">
                <div className="w-full lg:w-1/3 space-y-4">
                  <h3 className="font-bold text-gray-800">1. Select Driver</h3>
                  <div className="space-y-2">
                    {drivers.map(driver => (
                      <button
                        key={driver.id}
                        onClick={() => setSelectedDriver(driver.id)}
                        className={`w-full text-left p-4 rounded-lg border-2 transition-all ${selectedDriver === driver.id ? 'border-emerald-500 bg-emerald-50 ring-2 ring-emerald-200 ring-offset-1' : 'border-gray-200 hover:border-emerald-300'}`}
                      >
                        <div className="font-bold text-gray-900">{driver.name}</div>
                        <div className="text-xs text-gray-500">{driver.username}</div>
                        {driver.vehicleDetails && (
                           <div className="mt-2 text-[10px] font-bold text-gray-500 uppercase">
                              {driver.vehicleDetails.model} ({driver.vehicleDetails.registration})
                           </div>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
                
                <div className="w-full lg:w-2/3 space-y-4">
                  <div className="flex justify-between items-end">
                    <h3 className="font-bold text-gray-800">2. Assign Students</h3>
                    {selectedDriver && (
                      <button
                        onClick={handleSaveAssignments}
                        disabled={saving}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded font-bold shadow-sm flex items-center transition-colors text-sm"
                      >
                        {saving ? <Loader className="animate-spin mr-2" size={16} /> : <CheckCircle className="mr-2" size={16} />}
                        Save Assignments
                      </button>
                    )}
                  </div>
                  
                  {!selectedDriver ? (
                    <div className="border-2 border-dashed border-gray-200 rounded-lg p-10 text-center text-gray-400 font-medium">
                      Select a driver from the left to assign students.
                    </div>
                  ) : (
                    <div className="border rounded-lg overflow-hidden bg-white shadow-sm flex flex-col h-[500px]">
                      <div className="p-3 border-b bg-gray-50 relative">
                        <Search className="absolute left-6 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                        <input 
                          type="text" 
                          placeholder="Search students..." 
                          className="w-full pl-10 pr-4 py-2 border rounded text-sm focus:ring-2 focus:ring-emerald-500 outline-none"
                          value={searchQuery}
                          onChange={e => setSearchQuery(e.target.value)}
                        />
                      </div>
                      <div className="flex-1 overflow-y-auto p-0">
                        <ul className="divide-y divide-gray-100">
                          {filteredStudents.length === 0 ? (
                            <li className="p-6 text-center text-gray-500">No students found matching your search.</li>
                          ) : (
                            filteredStudents.map(student => (
                              <li 
                                key={student.id} 
                                className="flex items-center p-3 hover:bg-emerald-50 cursor-pointer transition-colors"
                                onClick={() => handleToggleAssignment(student.id)}
                              >
                                <input 
                                  type="checkbox" 
                                  className="w-5 h-5 accent-emerald-600 mr-4 cursor-pointer" 
                                  checked={driverAssignments.includes(student.id)}
                                  readOnly
                                />
                                <div>
                                  <p className="font-bold text-gray-900 text-sm">{student.studentName}</p>
                                </div>
                              </li>
                            ))
                          )}
                        </ul>
                      </div>
                      <div className="bg-gray-50 border-t p-3 text-sm font-bold text-gray-700 flex justify-between items-center">
                        <span>Total Assigned: {driverAssignments.length}</span>
                        {driverAssignments.length > 0 && (
                          <button 
                            onClick={() => setDriverAssignments([])} 
                            className="text-red-600 hover:text-red-800 text-xs underline"
                          >
                            Clear All
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
        {tab === 'opted' && (() => {
          // Robust check for opted transportation
          const isOpted = (s: any) => {
            const legacyVal = s['1782312752155'];
            const hasLegacy = legacyVal === true || String(legacyVal).toLowerCase() === 'yes';
            const hasField = findField(s, s.surveyRaw, 'transport', ['transport', 'യാത്ര', 'സൗകര്യം', 'വാഹനം'], true);
            return hasLegacy || hasField === true || String(hasField).toLowerCase() === 'yes';
          };

          const allOpted = allStudents;

          const optedStudents = allOpted.filter(s => {
            const fatherName = s.fatherName || findField(s, s.surveyRaw, 'fatherName', ['father', 'പിതാവ്', 'അച്ഛൻ', 'ഉപ്പ'], true) || '';
            const motherName = s.motherName || findField(s, s.surveyRaw, 'motherName', ['mother', 'മാതാവ്', 'അമ്മ', 'ഉമ്മ'], true) || '';
            const fatherPhone = s.fatherPhone || cleanContactNumber(findField(s, s.surveyRaw, 'fatherPhone', ['father phone', 'father mobile', 'ഫോൺ', 'മൊബൈൽ'], true) || getHouseholdPhone(s.surveyRaw, questions)) || s.phone || '';
            const motherPhone = s.motherPhone || cleanContactNumber(findField(s, s.surveyRaw, 'motherPhone', ['mother phone', 'mother mobile'], true)) || '';
            const loc = getHouseholdLocation(s.surveyRaw, questions) || findField(s, s.surveyRaw, 'location', ['location', 'zone', 'area', 'address']) || s.location || s['1782312786621'] || '';
            
            const assignment = assignmentsMap[s.id];

            // 1. Search filter
            if (optedSearchQuery.trim() !== '') {
              const q = optedSearchQuery.toLowerCase();
              const match = 
                s.studentName.toLowerCase().includes(q) || 
                s.parentName.toLowerCase().includes(q) || 
                fatherName.toLowerCase().includes(q) || 
                motherName.toLowerCase().includes(q) || 
                fatherPhone.includes(q) || 
                motherPhone.includes(q) || 
                loc.toLowerCase().includes(q);
              if (!match) return false;
            }

            // 2. Driver filter
            if (optedDriverFilter !== 'all') {
              if (optedDriverFilter === 'assigned') {
                if (!assignment) return false;
              } else if (optedDriverFilter === 'unassigned') {
                if (assignment) return false;
              } else {
                if (!assignment || assignment.id !== optedDriverFilter) return false;
              }
            }

            return true;
          });

          const totalOptedCount = allStudents.filter(isOpted).length;
          const assignedCount = allStudents.filter(s => isOpted(s) && !!assignmentsMap[s.id]).length;
          const unassignedCount = totalOptedCount - assignedCount;

          const handleExportOptedCSV = () => {
            const headers = [
              'Serial No',
              'Student Name',
              'Parent Name',
              'Father\'s Name',
              'Mother\'s Name',
              'Father\'s Phone',
              'Mother\'s Phone',
              'WhatsApp',
              'Location / Zone / Address',
              'Transport Opted',
              'Assigned Driver',
              'Assigned Vehicle'
            ];

            const rows = optedStudents.map(s => {
              const fatherName = s.fatherName || findField(s, s.surveyRaw, 'fatherName', ['father', 'പിതാവ്', 'അച്ഛൻ', 'ഉപ്പ'], true) || '';
              const motherName = s.motherName || findField(s, s.surveyRaw, 'motherName', ['mother', 'മാതാവ്', 'അമ്മ', 'ഉമ്മ'], true) || '';
              const fatherPhone = s.fatherPhone || cleanContactNumber(findField(s, s.surveyRaw, 'fatherPhone', ['father phone', 'father mobile', 'ഫോൺ', 'മൊബൈൽ'], true) || getHouseholdPhone(s.surveyRaw, questions)) || s.phone || '';
              const motherPhone = s.motherPhone || cleanContactNumber(findField(s, s.surveyRaw, 'motherPhone', ['mother phone', 'mother mobile'], true)) || '';
              const whatsapp = cleanContactNumber(findField(s, s.surveyRaw, 'whatsapp', ['whatsapp', 'വാട്സാപ്പ്', 'വാട്സ്'], true)) || s.whatsapp || '';
              const loc = getHouseholdLocation(s.surveyRaw, questions) || findField(s, s.surveyRaw, 'location', ['location', 'zone', 'area', 'address']) || s.location || s['1782312786621'] || '';
              
              const assignment = assignmentsMap[s.id];
              const assignedDriver = assignment ? assignment.name : 'Unassigned';
              const assignedVehicle = assignment?.vehicleDetails ? `${assignment.vehicleDetails.model} (${assignment.vehicleDetails.registration})` : 'N/A';
              const optedVal = isOpted(s) ? 'Yes' : 'No';

              return [
                `"${String(s.serialNo || '').replace(/"/g, '""')}"`,
                `"${String(s.studentName || '').replace(/"/g, '""')}"`,
                `"${String(s.parentName || '').replace(/"/g, '""')}"`,
                `"${String(fatherName).replace(/"/g, '""')}"`,
                `"${String(motherName).replace(/"/g, '""')}"`,
                `"${String(fatherPhone).replace(/"/g, '""')}"`,
                `"${String(motherPhone).replace(/"/g, '""')}"`,
                `"${String(whatsapp).replace(/"/g, '""')}"`,
                `"${String(loc).replace(/"/g, '""')}"`,
                `"${optedVal}"`,
                `"${String(assignedDriver).replace(/"/g, '""')}"`,
                `"${String(assignedVehicle).replace(/"/g, '""')}"`
              ].join(',');
            });

            const csvContent = [headers.join(','), ...rows].join('\n');
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.setAttribute("href", url);
            link.setAttribute("download", `transportation_complete_details_${new Date().toISOString().slice(0, 10)}.csv`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
          };

          const handleExportAllCSV = () => {
            const headers = [
              'Serial No',
              'Student Name',
              'Parent Name',
              'Father\'s Name',
              'Mother\'s Name',
              'Father\'s Phone',
              'Mother\'s Phone',
              'WhatsApp',
              'Location / Zone / Address',
              'Transport Opted',
              'Assigned Driver',
              'Assigned Vehicle'
            ];

            const rows = allStudents.map(s => {
              const fatherName = s.fatherName || findField(s, s.surveyRaw, 'fatherName', ['father', 'പിതാവ്', 'അച്ഛൻ', 'ഉപ്പ'], true) || '';
              const motherName = s.motherName || findField(s, s.surveyRaw, 'motherName', ['mother', 'മാതാവ്', 'അമ്മ', 'ഉമ്മ'], true) || '';
              const fatherPhone = s.fatherPhone || cleanContactNumber(findField(s, s.surveyRaw, 'fatherPhone', ['father phone', 'father mobile', 'ഫോൺ', 'മൊബൈൽ'], true) || getHouseholdPhone(s.surveyRaw, questions)) || s.phone || '';
              const motherPhone = s.motherPhone || cleanContactNumber(findField(s, s.surveyRaw, 'motherPhone', ['mother phone', 'mother mobile'], true)) || '';
              const whatsapp = cleanContactNumber(findField(s, s.surveyRaw, 'whatsapp', ['whatsapp', 'വാട്സാപ്പ്', 'വാട്സ്'], true)) || s.whatsapp || '';
              const loc = getHouseholdLocation(s.surveyRaw, questions) || findField(s, s.surveyRaw, 'location', ['location', 'zone', 'area', 'address']) || s.location || s['1782312786621'] || '';
              
              const assignment = assignmentsMap[s.id];
              const assignedDriver = assignment ? assignment.name : 'Unassigned';
              const assignedVehicle = assignment?.vehicleDetails ? `${assignment.vehicleDetails.model} (${assignment.vehicleDetails.registration})` : 'N/A';
              const opted = isOpted(s) ? 'Yes' : 'No';

              return [
                `"${String(s.serialNo || '').replace(/"/g, '""')}"`,
                `"${String(s.studentName || '').replace(/"/g, '""')}"`,
                `"${String(s.parentName || '').replace(/"/g, '""')}"`,
                `"${String(fatherName).replace(/"/g, '""')}"`,
                `"${String(motherName).replace(/"/g, '""')}"`,
                `"${String(fatherPhone).replace(/"/g, '""')}"`,
                `"${String(motherPhone).replace(/"/g, '""')}"`,
                `"${String(whatsapp).replace(/"/g, '""')}"`,
                `"${String(loc).replace(/"/g, '""')}"`,
                `"${opted}"`,
                `"${String(assignedDriver).replace(/"/g, '""')}"`,
                `"${String(assignedVehicle).replace(/"/g, '""')}"`
              ].join(',');
            });

            const csvContent = [headers.join(','), ...rows].join('\n');
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.setAttribute("href", url);
            link.setAttribute("download", `transportation_all_students_details_${new Date().toISOString().slice(0, 10)}.csv`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
          };

          return (
            <div className="space-y-6">
              {/* Transportation Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-slate-50 border border-slate-100 p-4 rounded-xl flex items-center justify-between">
                  <div>
                    <span className="text-xs font-black text-slate-500 uppercase tracking-wider block">Opted Transportation</span>
                    <span className="text-2xl font-black text-slate-900">{totalOptedCount} Students</span>
                  </div>
                  <div className="p-3 rounded-full bg-slate-100 text-slate-700">
                    <Bus size={24} />
                  </div>
                </div>
                <div className="bg-emerald-50 border border-emerald-100 p-4 rounded-xl flex items-center justify-between">
                  <div>
                    <span className="text-xs font-black text-emerald-600 uppercase tracking-wider block">Driver Assigned</span>
                    <span className="text-2xl font-black text-emerald-800">{assignedCount} Students</span>
                  </div>
                  <div className="p-3 rounded-full bg-emerald-100 text-emerald-700">
                    <CheckCircle size={24} />
                  </div>
                </div>
                <div className="bg-amber-50 border border-amber-100 p-4 rounded-xl flex items-center justify-between">
                  <div>
                    <span className="text-xs font-black text-amber-600 uppercase tracking-wider block">Pending Assignment</span>
                    <span className="text-2xl font-black text-amber-800">{unassignedCount} Students</span>
                  </div>
                  <div className="p-3 rounded-full bg-amber-100 text-amber-700">
                    <Info size={24} />
                  </div>
                </div>
              </div>

              {/* Advanced Filter Control Bar */}
              <div className="flex flex-col md:flex-row justify-between items-stretch md:items-center gap-4 bg-gray-50 p-4 rounded-xl border">
                <div className="flex-1 flex flex-col sm:flex-row gap-3">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                    <input 
                      type="text" 
                      placeholder="Search by student, parent, phone or location..." 
                      className="w-full pl-9 pr-4 py-2 border rounded-lg text-sm bg-white focus:ring-2 focus:ring-emerald-500 outline-none font-medium"
                      value={optedSearchQuery}
                      onChange={e => setOptedSearchQuery(e.target.value)}
                    />
                  </div>
                  <div className="w-full sm:w-56">
                    <select
                      className="w-full border rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-emerald-500 outline-none font-bold text-gray-700"
                      value={optedDriverFilter}
                      onChange={e => setOptedDriverFilter(e.target.value)}
                    >
                      <option value="all">🚙 All Assignment States</option>
                      <option value="assigned">✅ Assigned to Driver</option>
                      <option value="unassigned">⚠️ Unassigned / Pending</option>
                      {drivers.map(dr => (
                        <option key={dr.id} value={dr.id}>👤 {dr.name} ({dr.vehicleDetails?.registration || 'No Reg'})</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={handleExportOptedCSV}
                    disabled={optedStudents.length === 0}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-5 py-2.5 rounded-xl flex items-center justify-center space-x-2 shadow-sm transition disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                  >
                    <Download size={18} />
                    <span>Export Opted CSV ({optedStudents.length})</span>
                  </button>
                  <button
                    onClick={handleExportAllCSV}
                    disabled={allStudents.length === 0}
                    className="bg-slate-700 hover:bg-slate-800 text-white font-bold px-5 py-2.5 rounded-xl flex items-center justify-center space-x-2 shadow-sm transition disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                  >
                    <Download size={18} />
                    <span>Export All Students CSV ({allStudents.length})</span>
                  </button>
                </div>
              </div>

              {/* Complete Transport Details Table */}
              <div className="overflow-x-auto bg-white border border-gray-200 rounded-xl shadow-sm">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-slate-900 text-white">
                    <tr>
                      <th className="px-4 py-3.5 text-left text-xs font-black uppercase tracking-wider">Sl No</th>
                      <th className="px-5 py-3.5 text-left text-xs font-black uppercase tracking-wider">Student & Parents</th>
                      <th className="px-5 py-3.5 text-left text-xs font-black uppercase tracking-wider">Contact Information</th>
                      <th className="px-5 py-3.5 text-left text-xs font-black uppercase tracking-wider">Full Location / Address</th>
                      <th className="px-5 py-3.5 text-left text-xs font-black uppercase tracking-wider">Opted Transport</th>
                      <th className="px-5 py-3.5 text-left text-xs font-black uppercase tracking-wider">Assigned Trip Status</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {optedStudents.map((s, idx) => {
                      const fatherName = s.fatherName || findField(s, s.surveyRaw, 'fatherName', ['father', 'പിതാവ്', 'അച്ഛൻ', 'ഉപ്പ'], true) || '';
                      const motherName = s.motherName || findField(s, s.surveyRaw, 'motherName', ['mother', 'മാതാവ്', 'അമ്മ', 'ഉമ്മ'], true) || '';
                      const fatherPhone = s.fatherPhone || cleanContactNumber(findField(s, s.surveyRaw, 'fatherPhone', ['father phone', 'father mobile', 'ഫോൺ', 'മൊബൈൽ'], true) || getHouseholdPhone(s.surveyRaw, questions)) || s.phone || '';
                      const motherPhone = s.motherPhone || cleanContactNumber(findField(s, s.surveyRaw, 'motherPhone', ['mother phone', 'mother mobile'], true)) || '';
                      const whatsapp = cleanContactNumber(findField(s, s.surveyRaw, 'whatsapp', ['whatsapp', 'വാട്സാപ്പ്', 'വാട്സ്'], true)) || s.whatsapp || '';
                      const loc = getHouseholdLocation(s.surveyRaw, questions) || findField(s, s.surveyRaw, 'location', ['location', 'zone', 'area', 'address']) || s.location || s['1782312786621'] || '';
                      
                      const assignment = assignmentsMap[s.id];
                      const opted = isOpted(s);

                      return (
                        <tr key={s.id || idx} className="hover:bg-gray-50/75 transition-colors">
                          <td className="px-4 py-4 whitespace-nowrap text-sm font-black text-gray-500">
                            {s.serialNo || idx + 1}
                          </td>
                          <td className="px-5 py-4">
                            <span className="font-bold text-gray-900 text-sm block">{s.studentName}</span>
                            <div className="text-[11px] text-gray-500 space-y-0.5 mt-0.5 font-medium">
                              {fatherName && <p><strong>Father:</strong> {fatherName}</p>}
                              {motherName && <p><strong>Mother:</strong> {motherName}</p>}
                              {!fatherName && !motherName && <p><strong>Parent:</strong> {s.parentName || 'N/A'}</p>}
                            </div>
                          </td>
                          <td className="px-5 py-4">
                            <div className="space-y-1.5 text-xs">
                              {fatherPhone && (
                                <div className="flex items-center space-x-1.5">
                                  <span className="text-[10px] font-black uppercase text-slate-400 bg-slate-100 px-1 rounded">Father</span>
                                  <a href={`tel:${fatherPhone}`} className="text-emerald-600 hover:underline font-bold flex items-center">
                                    <PhoneCall size={10} className="mr-1" /> {fatherPhone}
                                  </a>
                                </div>
                              )}
                              {motherPhone && (
                                <div className="flex items-center space-x-1.5">
                                  <span className="text-[10px] font-black uppercase text-rose-400 bg-rose-50 px-1 rounded">Mother</span>
                                  <a href={`tel:${motherPhone}`} className="text-rose-600 hover:underline font-bold flex items-center">
                                    <PhoneCall size={10} className="mr-1" /> {motherPhone}
                                  </a>
                                </div>
                              )}
                              {whatsapp && (
                                <div className="flex items-center space-x-1.5">
                                  <span className="text-[10px] font-black uppercase text-teal-500 bg-teal-50 px-1 rounded">WhatsApp</span>
                                  <a href={`https://wa.me/${whatsapp.replace('+', '')}`} target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:underline font-bold flex items-center">
                                    {whatsapp}
                                  </a>
                                </div>
                              )}
                              {!fatherPhone && !motherPhone && !whatsapp && (
                                <span className="text-gray-400 italic">No contact details</span>
                              )}
                            </div>
                          </td>
                          <td className="px-5 py-4">
                            <div className="flex items-start text-xs text-gray-700 max-w-xs">
                              <MapPin size={14} className="text-emerald-600 shrink-0 mt-0.5 mr-1.5" />
                              <span className="font-medium line-clamp-2" title={loc}>{loc || 'N/A'}</span>
                            </div>
                          </td>
                          <td className="px-5 py-4 whitespace-nowrap">
                            {opted ? (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-emerald-100 text-emerald-800">
                                Yes
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-gray-100 text-gray-800">
                                No
                              </span>
                            )}
                          </td>
                          <td className="px-5 py-4 whitespace-nowrap">
                            {assignment ? (
                              <div className="space-y-1">
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-emerald-100 text-emerald-800">
                                  <CheckCircle size={10} className="mr-1" />
                                  Assigned
                                </span>
                                <div className="text-xs text-gray-700">
                                  <p className="font-bold">{assignment.name}</p>
                                  {assignment.vehicleDetails && (
                                    <p className="text-[10px] text-gray-500 font-medium">{assignment.vehicleDetails.model} ({assignment.vehicleDetails.registration})</p>
                                  )}
                                </div>
                              </div>
                            ) : (
                              <div>
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-amber-100 text-amber-800">
                                  <Info size={10} className="mr-1" />
                                  Unassigned
                                </span>
                                <p className="text-[10px] text-gray-400 mt-1">Pending route assignment</p>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}

                    {optedStudents.length === 0 && (
                      <tr>
                        <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                          <Bus className="mx-auto text-gray-300 mb-2" size={36} />
                          <p className="font-bold text-gray-600">No matching students found</p>
                          <p className="text-gray-400 text-sm mt-0.5">Try widening your search or changing the filter.</p>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
};

export default BusManager;
